import { apiFetch } from './client';
import type { Periodo } from './tienda.api';

/** Cobros: los tickets de lo que se vio pagar (o quedó en pasar) en los chats. Prefijo /api/cobros */

export type EstadoDeTicket = 'ABIERTO' | 'CONFIRMADO' | 'DESCARTADO';
export type OrigenDeTicket = 'COMPROBANTE' | 'TEXTO' | 'PROMESA';
export type MotivoDescarte = 'no_pago' | 'no_vino' | 'no_era_pago' | 'duplicado' | 'otro';

export const MOTIVOS: Array<{ id: MotivoDescarte; etiqueta: string }> = [
  { id: 'no_pago', etiqueta: 'No pagó' },
  { id: 'no_vino', etiqueta: 'No vino' },
  { id: 'no_era_pago', etiqueta: 'No era un pago' },
  { id: 'duplicado', etiqueta: 'Duplicado' },
  { id: 'otro', etiqueta: 'Otro motivo' },
];

export const ORIGEN: Record<OrigenDeTicket, string> = {
  COMPROBANTE: 'Mandó comprobante',
  TEXTO: 'Dijo que pagó',
  PROMESA: 'Quedó en pasar',
};

export interface ProductoDelTicket {
  productId: string | null;
  nombre: string;
  cantidad: number;
  precio: number | null;
}

export interface EntradaDeHistorial {
  at: string;
  quien: string;
  que: string;
}

export interface Ticket {
  id: string;
  numero: string;
  estado: EstadoDeTicket;
  origen: OrigenDeTicket;
  conversationId: string;
  asignadaAId: string | null;
  anuncioTitulo: string | null;
  evidencia: string | null;
  montoDetectado: number | null;
  banco: string | null;
  referencia: string | null;
  productosJson: ProductoDelTicket[] | null;
  faltaSaber: string | null;
  revisarEl: string | null;
  preguntadoAt: string | null;
  respuesta: string | null;
  avisos: number;
  escaladoAt: string | null;
  saleId: string | null;
  montoConfirmado: number | null;
  motivo: MotivoDescarte | null;
  historial: EntradaDeHistorial[];
  creadoAt: string;
  cerradoAt: string | null;
  cliente: string;
  telefono: string | null;
  vendedora: string | null;
  ventaNumero: string | null;
  evidenciaUrl: string | null;
  evidenciaTipo: string | null;
}

export interface Resumen {
  facturado: number;
  ventas: number;
  comisionPorcentaje: number | null;
  comision: number | null;
  pagosVistos: number;
  montoVisto: number;
  confirmados: number;
  montoConfirmado: number;
  descartados: number;
  abiertos: number;
  montoAbierto: number;
}

export interface Config {
  comisionPorcentaje: number | null;
  leerConIA: boolean;
  perseguirCadaHoras: number;
  escalarTrasHoras: number;
  maxAvisos: number;
}

export function listar(query: { estado?: EstadoDeTicket; periodo?: Periodo | 'todo' }): Promise<Ticket[]> {
  return apiFetch<Ticket[]>('/api/cobros', { query });
}

export function resumen(periodo: Periodo | 'todo'): Promise<Resumen> {
  return apiFetch<Resumen>('/api/cobros/resumen', { query: { periodo } });
}

export function config(): Promise<Config> {
  return apiFetch<Config>('/api/cobros/config');
}

export function guardarConfig(body: Partial<Config>): Promise<Config> {
  return apiFetch<Config>('/api/cobros/config', { method: 'PUT', body });
}

export function ventasParaVincular(id: string): Promise<Array<{ id: string; numero: string; total: number; confirmedAt: string }>> {
  return apiFetch(`/api/cobros/${id}/ventas`);
}

export type Accion =
  | { tipo: 'vincular'; saleId: string }
  | { tipo: 'descartar'; motivo: MotivoDescarte; nota?: string | null }
  | { tipo: 'nota'; nota: string }
  | { tipo: 'reabrir' };

export function accion(id: string, body: Accion): Promise<Ticket> {
  return apiFetch<Ticket>(`/api/cobros/${id}/accion`, { method: 'POST', body });
}

/** Para Excel: una fila por ticket con su historial en una celda. */
export function aCsv(tickets: Ticket[]): string {
  const celda = (v: unknown) => {
    // Lo que escribió una clienta (su nombre, su mensaje) no puede volverse fórmula en Excel.
    const s = String(v ?? '').replace(/^[=+\-@\t\r]/, "'$&");
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const cols: Array<[string, (t: Ticket) => unknown]> = [
    ['Ticket', (t) => t.numero],
    ['Fecha', (t) => t.creadoAt],
    ['Estado', (t) => t.estado],
    ['Qué pasó', (t) => ORIGEN[t.origen]],
    ['Cliente', (t) => t.cliente],
    ['Teléfono', (t) => t.telefono],
    ['Vendedora', (t) => t.vendedora],
    ['Anuncio', (t) => t.anuncioTitulo],
    ['Monto visto', (t) => t.montoDetectado],
    ['Banco', (t) => t.banco],
    ['Productos', (t) => (t.productosJson ?? []).map((p) => `${p.cantidad}× ${p.nombre}`).join(' · ')],
    ['Falta saber', (t) => t.faltaSaber],
    ['Venta', (t) => t.ventaNumero],
    ['Monto confirmado', (t) => t.montoConfirmado],
    ['Motivo', (t) => t.motivo],
    ['Avisos', (t) => t.avisos],
    ['Historial', (t) => t.historial.map((h) => `${h.at.slice(0, 16).replace('T', ' ')} ${h.quien}: ${h.que}`).join(' | ')],
  ];
  const lineas = [cols.map(([n]) => celda(n)).join(',')];
  for (const t of tickets) lineas.push(cols.map(([, f]) => celda(f(t))).join(','));
  return '﻿' + lineas.join('\r\n') + '\r\n';
}
